import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type SitemapEntry } from '@nasa-gcn/remix-seo/build/types'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, type LoaderFunctionArgs } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import Instagram from '#app/components/logos/instagram.tsx'
import MarketingImage from '#app/components/marketing/image.tsx'
import MoreInformationDialog from '#app/components/products/more-info.tsx'
import OtherProductsSection from '#app/components/products/other-products.tsx'
import { PlainImage } from '#app/components/ui/image.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { Collection } from '#app/enums/collection.ts'
import { useChangeBackgroundColor } from '#app/hooks/use-change-background-color.ts'
import {
	AMBIENT_CONFIGURATIONS,
	CONTINUITY_OPTIONS,
} from '#app/routes/admin+/materials+/types.ts'
import { BUSINESS_NAME } from '#app/utils/business.ts'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import {
	getOGLocales,
	i18n,
	type ISupportedLanguage,
	LocalizedLink,
} from '#app/utils/i18n.tsx'
import {
	cn,
	getMaterialAmbientImgSrc,
	getMaterialDesignImgSrc,
} from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import { usePublicContext } from '../../__public-context'
import { type Route } from './+types/$slug'

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const products = await prisma.collection.findMany({
			where: {
				published: true,
			},
		})

		const routes = new Set<SitemapEntry>()

		i18n.supportedLngs.forEach((lang) => {
			products.forEach((product) => {
				routes.add({
					route: `/${lang}/products/${product.slug}`,
					priority: 0.8,
					changefreq: 'weekly',
					lastmod: product.updatedAt.toISOString(),
				})
			})
		})

		return Array.from(routes)
	}),
}

export async function loader({ params }: LoaderFunctionArgs) {
	let { slug, lang } = params

	if (!lang) {
		lang = i18n.fallbackLng
	}

	if (!slug) {
		throw new Response('Product not specified. Missing slug parameter.', {
			status: 404,
		})
	}

	const product = await prisma.collection.findFirst({
		where: {
			slug,
			published: true,
			translations: {
				some: {
					languageCode: lang,
				},
			},
		},
		include: {
			designsImages: {
				take: 1,
				select: {
					id: true,
					altText: true,
					// Only select ID and altText - blob will be loaded on-demand via resource route
				},
			},
			ambientImages: {
				select: {
					id: true,
					altText: true,
					order: true,
					// Only select ID, altText, and order - blob will be loaded on-demand via resource route
					// This is critical for performance as products can have 5+ large ambient images
				},
			},
			translations: {
				where: {
					languageCode: lang,
				},
				take: 1,
			},
			characteristics: {
				select: {
					instagramLink: true,
					ambientConfiguration: true,
					bookmatched: true,
					fourthDimension: true,
					continuity: true,
					collections: true,
				},
			},
			variants: {
				include: {
					size: {
						select: {
							size_eu: true,
							size_us: true,
						},
					},
					width: {
						select: {
							width: true,
						},
					},
					finish: {
						select: {
							translations: {
								select: {
									name: true,
								},
								where: {
									languageCode: lang,
								},
							},
						},
					},
				},
			},
		},
	})

	if (!product) {
		throw new Response('Product not found.', { status: 404 })
	}

	const otherProducts = await prisma.collection.findMany({
		take: 5,
		where: {
			collectionTypologyId: product.collectionTypologyId,
			slug: {
				not: product.slug,
			},
			published: true,
		},
		select: {
			name: true,
			slug: true,
			designsImages: {
				take: 1,
				select: {
					id: true,
					altText: true,
				},
				orderBy: {
					order: 'asc',
				},
			},
		},
	})

	return {
		product,
		otherProducts,
	}
}

export const meta = ({ data, params }: Route.MetaArgs) => {
	// @ts-ignore
	const { product } = data
	const { lang } = params

	return [
		{
			title: `${product.name} - ${BUSINESS_NAME}`,
		},
		{
			name: 'description',
			content: product.translations[0]?.description,
		},
		{
			name: 'og:title',
			content: `${product.name} - ${BUSINESS_NAME}`,
		},
		{
			name: 'og:type',
			content: 'website',
		},
		{
			name: 'og:image',
			content: `${BASE_URL}${getMaterialDesignImgSrc(product?.designsImages[0]?.id!)}`,
		},
		{
			name: 'og:image:alt',
			content: `${product.name} | ${BUSINESS_NAME}`,
		},
		...getOGLocales((lang ?? 'en') as ISupportedLanguage),
		{
			name: 'og:site_name',
			content: BUSINESS_NAME,
		},
	]
}

function VariantItemsSection({
	title,
	items,
}: {
	title: string
	items: string[] | string[][]
}) {
	return (
		<div className="flex flex-col gap-sm md:gap-md">
			<h2 className="border-b border-brand-dusk font-semibold uppercase tracking-[0.2em] text-brand-dusk">
				{title}
			</h2>

			<div className={cn('flex flex-col gap-xxs font-light')}>
				{Array.isArray(items[0]) ? (
					<>
						{items.map((i, index) => (
							<div key={index} className="flex flex-wrap gap-xxs">
								{(i as string[]).map((it, idx, arr) => (
									<>
										<p key={index}>{it}</p>
										{idx !== arr.length - 1 && <span>|</span>}
									</>
								))}
							</div>
						))}
					</>
				) : (
					<div className="flex flex-wrap gap-xxs">
						{(items as string[]).map((it, idx, arr) => (
							<>
								<p key={idx}>{it}</p>
								{idx !== arr.length - 1 && <span>|</span>}
							</>
						))}
					</div>
				)}
			</div>
		</div>
	)
}

export default function ProductPage({ loaderData }: Route.ComponentProps) {
	const {
		navigation: { route },
	} = usePublicContext()
	const { t } = useTranslation('products')
	const ref = useChangeBackgroundColor(extendedTheme.colors.background)

	useEffect(() => {
		if (!loaderData) return

		route.setContent([
			{
				label: `${t('title')}.`,
				to: routes.public.products.list.route({}),
			},
			{
				label: loaderData.product.name,
				to: routes.public.products.view.route({
					slug: loaderData.product.slug,
				}),
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loaderData.product.slug])

	if (!loaderData) return null

	const { product, otherProducts } = loaderData

	return (
		<>
			{/* Description  */}
			<div className="mx-sm py-xl text-sm xl:text-base 2xl:text-[0.6vw] 2xl:leading-[1.2]">
				<Text
					className="mx-auto max-w-[570px] xl:max-w-[680px] 2xl:max-w-[35vw]"
					as={'p'}
					size={'small'}
					weight={'light'}
				>
					{product.translations[0]?.description}
				</Text>

				<div className="mx-auto mt-xl grid max-w-[1150px] grid-cols-1 gap-xxs text-body-2xs md:grid-cols-3 md:gap-sm xl:text-body-xs 2xl:max-w-[45vw]">
					<VariantItemsSection
						title={t('public.filters.size')}
						items={Array.from(
							new Set(product.variants.map((variant) => variant.size.size_eu)),
						).map((variant) => {
							const size = product.variants.find(
								(v) => v.size.size_eu === variant,
							)!.size

							return [size.size_eu, size.size_us]
						})}
					/>
					<VariantItemsSection
						title={t('public.filters.width')}
						items={Array.from(
							new Set(
								product.variants.map((variant) => `${variant.width.width} mm`),
							),
						)}
					/>
					<VariantItemsSection
						title={t('public.filters.finish')}
						items={Array.from(
							new Set(
								product.variants.map(
									(variant) => variant.finish.translations[0]!.name,
								),
							),
						)}
					/>
				</div>

				{product.characteristics?.collections?.includes(
					Collection.DRESS_CODE.code,
				) && (
					<Text
						className="mx-auto mt-xl w-full max-w-[1150px] 2xl:max-w-[45vw]"
						as={'p'}
						size={'small'}
						weight={'light'}
					>
						*
						<Trans
							t={t}
							i18nKey={'public.product.dressCode'}
							components={[
								<LocalizedLink
									className={'font-medium underline'}
									to={routes.public.landings.dressYourLook.route({})}
									key={0}
								/>,
							]}
						></Trans>
					</Text>
				)}
			</div>

			{/* Gallery  */}
			<div
				ref={ref}
				className={cn(
					// General
					'mx-auto grid max-w-[1800px]',
					// Mobile + Tablet
					'mb-[100px] grid-cols-6 gap-y-[48px] sm:gap-y-[2.5em]',
					// Desktop
					'md:grid-cols-12 md:gap-x-[48px] md:gap-y-[4em] lg:gap-x-[4%] lg:gap-y-[6em] 2xl:gap-x-[8%] 2xl:gap-y-[8em]',
					product.ambientImages.length === 5 && 'md:mb-[150px] 2xl:mb-[250px]',
				)}
			>
				<div
					className={cn(
						// General
						'grid grid-cols-subgrid gap-y-[48px]',
						// Mobile + Tablet
						'col-span-6',
						// Desktop
						'md:col-span-12 md:grid-rows-5 md:gap-x-[48px] lg:gap-x-[4%] 2xl:gap-x-[8%]',
					)}
				>
					<div
						className={cn(
							// Mobile + Tablet
							'col-span-4 col-start-2',
							// Desktop
							'md:col-span-6 md:col-start-2 md:row-span-5',
						)}
					>
						<PlainImage
							alt={
								product.ambientImages[0]?.altText ??
								`Ambiente de ${product.name}`
							}
							className="aspect-auto h-full"
							src={getMaterialAmbientImgSrc(product.ambientImages[0]!.id)}
						/>
					</div>
					<div
						className={cn(
							// Mobile + Tablet
							'col-span-4 col-start-2',
							// Desktop
							'md:col-start-8 md:row-span-4 md:row-start-2',
						)}
					>
						<div className="relative flex h-full w-full flex-shrink flex-grow flex-col gap-sm xl:gap-xl">
							<div className="flex justify-between gap-sm">
								<h2 className="tracking-[0.2em] text-brand-dusk">
									/ {product.name} /
								</h2>
								<div className="min-w-fit">
									<MoreInformationDialog
										product={{
											name: product.name,
											variants: product.variants,
											characteristics: {
												fourthDimension:
													product.characteristics?.fourthDimension ?? false,
												continuity:
													product.characteristics?.continuity ??
													CONTINUITY_OPTIONS.NONE,
												bookmatched:
													product.characteristics?.bookmatched ?? false,
											},
										}}
									/>
								</div>
							</div>
							<PlainImage
								className="aspect-auto h-full w-full"
								alt={product.name}
								src={getMaterialDesignImgSrc(product.designsImages[0]!.id)}
							/>
						</div>
					</div>
				</div>

				{product.ambientImages.length > 1 && (
					<div
						className={cn(
							// General
							'grid grid-cols-subgrid gap-y-[48px]',
							// Mobile + Tablet (6 columns)
							'col-span-6',
							// Desktop (12 columns), md: breakpoint
							'md:col-span-12 md:grid-rows-[repeat(12,4em)] md:gap-x-[48px] md:gap-y-0 lg:grid-rows-[repeat(12,5em)] lg:gap-x-[4%] 2xl:grid-rows-[repeat(12,8em)] 2xl:gap-x-[8%]',
						)}
					>
						{product.ambientImages
							.sort((a, b) => a.order - b.order)
							.slice(1)
							.map((image, index) => {
								const hasInstagramLink =
									(index === 2 &&
										product.characteristics?.instagramLink &&
										product.characteristics?.ambientConfiguration ===
											AMBIENT_CONFIGURATIONS['4_LEFT']) ||
									(index === 1 &&
										product.characteristics?.instagramLink &&
										product.characteristics?.ambientConfiguration ===
											AMBIENT_CONFIGURATIONS['3_BALANCED']) ||
									(index === 1 &&
										product.characteristics?.instagramLink &&
										product.characteristics?.ambientConfiguration ===
											AMBIENT_CONFIGURATIONS['2_VER_HOR'])
								const Comp = hasInstagramLink ? Link : 'div'

								return (
									<Comp
										key={image.id}
										to={
											hasInstagramLink
												? (product.characteristics?.instagramLink ?? '#')
												: '#'
										}
										className={cn(
											// General
											'group relative',
											// Mobile + Tablet
											'col-span-4 col-start-2',
											// Desktop
											// 2 images centered
											product.ambientImages.length === 2 &&
												product.characteristics?.ambientConfiguration ===
													AMBIENT_CONFIGURATIONS['1_CENTER_LEFT'] &&
												index === 0 &&
												'md:col-span-6 md:col-start-3 md:row-span-7 md:row-start-2',

											// 2 images right
											product.ambientImages.length === 2 &&
												product.characteristics?.ambientConfiguration ===
													AMBIENT_CONFIGURATIONS['1_RIGHT'] &&
												index === 0 &&
												'md:col-span-8 md:col-start-5 md:row-span-8 md:row-start-2',

											// 3 images
											product.ambientImages.length === 3 &&
												index === 0 &&
												'md:col-span-7 md:col-start-6 md:row-span-6 md:row-start-2',
											product.ambientImages.length === 3 &&
												index === 1 &&
												'md:col-span-4 md:col-start-2 md:row-span-7 md:row-start-4',

											// 4 images
											product.ambientImages.length === 4 &&
												index === 0 &&
												'md:col-span-6 md:col-start-7 md:row-span-5 md:row-start-1',
											product.ambientImages.length === 4 &&
												index === 1 &&
												'md:col-span-4 md:col-start-2 md:row-span-7 md:row-start-4',
											product.ambientImages.length === 4 &&
												index === 2 &&
												'md:col-span-5 md:col-start-6 md:row-span-6 md:row-start-7',

											// 5 images
											product.ambientImages.length === 5 &&
												index === 0 &&
												'md:col-span-6 md:col-start-1 md:row-span-5 md:row-start-1 md:max-h-[20em] lg:max-h-[30em] 2xl:max-h-[40em]',
											product.ambientImages.length === 5 &&
												index === 1 &&
												'md:col-start-8 md:row-span-3 md:row-start-2',
											product.ambientImages.length === 5 &&
												index === 2 &&
												'md:col-span-5 md:col-start-2 md:row-span-7 md:row-start-7 lg:col-span-4 lg:col-start-3',
											product.ambientImages.length === 5 &&
												index === 3 &&
												'md:col-span-6 md:col-start-7 md:row-span-5 md:row-start-7',
										)}
									>
										{hasInstagramLink && (
											<div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100">
												<Instagram className="w-[15%] fill-white" />
											</div>
										)}
										<MarketingImage
											className={cn(
												'aspect-auto h-full w-full',
												product.ambientImages.length === 5 &&
													index === 1 &&
													'!aspect-square',
											)}
											alt={`${product.name} - ${image.altText} - ${index + 2}`}
											src={getMaterialAmbientImgSrc(image.id)}
										/>
									</Comp>
								)
							})}
					</div>
				)}
			</div>

			{/* Other products */}
			<OtherProductsSection products={otherProducts} />
		</>
	)
}
